<template>
  <main class="constra-page">

    <!-- 1 — HERO -->
    <section class="hero">
      <div class="hero-dot-grid"></div>
      <div class="hero-glow"></div>
      <div class="section-container hero-inner">
        <div class="hero-wordmark">
          <img src="@/assets/img/constra/32543234_transparentO-4.png" alt="CONSTRA" class="hero-logo-img" />
        </div>
        <div class="hero-eyebrow">AI-Powered Construction Operations · by Bandhoo</div>
        <h1 class="hero-headline">AI-Based Construction<br>Management Solution</h1>
        <p class="hero-sub">Powered by Drones &amp; 360° Cameras</p>
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSdQditzMzSBuq2J2urqc2OR28cjAFdTitdSHlOyXdHp6N2rfQ/viewform" target="_blank" class="cta-btn">
          Request Demo
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M3 9h12M11 5l4 4-4 4" stroke="#003366" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <div class="hero-stats">
          <div v-for="s in heroStats" :key="s.val" class="hero-stat">
            <div class="hero-stat-val">{{ s.val }}</div>
            <div class="hero-stat-txt">{{ s.txt }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2 — CLIENT LOGOS -->
    <section class="client-logos-section">
      <InfiniteScroll :clients="constraClients" />
    </section>

    <!-- 3 — PROBLEM STATEMENT -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">The Problem</div>
          <h2>Manual Reporting is Cumbersome</h2>
          <p>Project updates fail at scale. Constra needs zero manual entry — powering real-time visibility automatically.</p>
          <div class="section-rule"></div>
        </div>
        <div class="scale-grid">
          <div v-for="s in scaleStats" :key="s.num" class="scale-card" :style="{ borderLeftColor: s.color }">
            <div class="scale-num" :style="{ color: s.color }">{{ s.num }}</div>
            <div class="scale-label">{{ s.label }}</div>
            <div class="scale-detail">{{ s.detail }}</div>
          </div>
        </div>
        <div class="problem-callout">
          <div class="callout-text">
            <div class="callout-eyebrow">CONSTRA SOLVES THIS</div>
            <p>Automatic updates from every drone &amp; 360° camera capture. No forms. No spreadsheets.</p>
          </div>
          <div class="callout-zero">
            <div class="zero-num">0</div>
            <div class="zero-label">MANUAL ENTRIES</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4 — OPS ARCHITECTURE -->
    <section class="section bg-white">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Architecture</div>
          <h2>Project Operations Management</h2>
          <p>Best-practice tool stack — third-party integrations feeding into CONSTRA modules</p>
          <div class="section-rule"></div>
        </div>
        <div class="ops-labels">
          <div class="ops-label-third">Third-party Tools</div>
          <div class="ops-label-constra">Powered by CONSTRA</div>
        </div>
        <div class="ops-grid">
          <div
            v-for="step in opsSteps"
            :key="step.n"
            class="ops-step"
            :class="step.type === 'constra' ? 'ops-step--constra' : 'ops-step--third'"
          >
            <div class="ops-num" :class="step.type === 'constra' ? 'ops-num--constra' : 'ops-num--third'">{{ step.n }}</div>
            <div class="ops-title" :class="step.type === 'constra' ? 'ops-title--constra' : ''">{{ step.title }}</div>
            <div class="ops-sub" :class="step.type === 'constra' ? 'ops-sub--constra' : ''">{{ step.sub }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 5 — 12 BENEFITS -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Benefits</div>
          <h2>12 Benefits of Using Constra</h2>
          <p>Comprehensive AI-powered capabilities covering every phase of construction</p>
          <div class="section-rule"></div>
        </div>
        <div class="benefits-grid">
          <div v-for="b in benefits" :key="b.n" class="benefit-card">
            <div class="benefit-watermark">{{ String(b.n).padStart(2, '0') }}</div>
            <div class="benefit-badge">{{ b.n }}</div>
            <h3 class="benefit-title">{{ b.title }}</h3>
            <p class="benefit-desc">{{ b.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 6 — CAPTURE FREQUENCY TABLE -->
    <section class="section bg-white">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Capture Plan</div>
          <h2>Capture Frequency Across Stages</h2>
          <p>Drone and 360° camera capture schedules — from first design to final handover</p>
          <div class="section-rule"></div>
        </div>
        <div class="table-wrap">
          <table class="capture-table">
            <thead>
              <tr>
                <th class="table-row-label-th"></th>
                <th v-for="(stage, i) in stages" :key="stage" :style="{ background: stageStyles[i].bg, color: stageStyles[i].col }">{{ stage }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="table-row-label">
                  <div>🚁 Drone</div>
                  <div class="table-row-sub">Captures</div>
                </td>
                <td
                  v-for="(val, i) in droneFreq"
                  :key="i"
                  class="table-freq-cell"
                  :class="val === '—' ? 'freq-empty' : 'freq-drone'"
                >{{ val }}</td>
              </tr>
              <tr>
                <td class="table-row-label">
                  <div>📷 360° Camera</div>
                  <div class="table-row-sub">Captures</div>
                </td>
                <td
                  v-for="(val, i) in camFreq"
                  :key="i"
                  class="table-freq-cell"
                  :class="val === '—' ? 'freq-empty' : 'freq-cam'"
                >{{ val }}</td>
              </tr>
              <tr>
                <td class="table-row-label">
                  <div>✦ Key Features</div>
                  <div class="table-row-sub">per stage</div>
                </td>
                <td v-for="(tags, i) in tableFeatures" :key="i" class="table-feat-cell">
                  <span v-for="tag in tags" :key="tag" class="feat-tag">{{ tag }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 7 — DEPARTMENT BENEFITS -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Stakeholders</div>
          <h2>Every Team Connected Through CONSTRA</h2>
          <p>A single visual source of truth — each department works from the same captures and reports</p>
          <div class="section-rule"></div>
        </div>

        <!-- Desktop: SVG network graph -->
        <div class="dept-figure">
          <svg viewBox="0 0 1360 1012" class="dept-svg" role="img" aria-label="Interconnected diagram of six teams connected through CONSTRA">
            <defs>
              <linearGradient v-for="arc in deptArcPaths" :key="arc.gradId"
                :id="arc.gradId" gradientUnits="userSpaceOnUse"
                :x1="arc.x1" :y1="arc.y1" :x2="arc.x2" :y2="arc.y2">
                <stop offset="0%" :stop-color="arc.startColor"/>
                <stop offset="100%" :stop-color="arc.endColor"/>
              </linearGradient>
            </defs>

            <!-- inner dashed web -->
            <line v-for="([aId, bId], i) in deptInnerPairs" :key="'inner'+i"
              :x1="deptTeamMap[aId].cx" :y1="deptTeamMap[aId].cy"
              :x2="deptTeamMap[bId].cx" :y2="deptTeamMap[bId].cy"
              stroke="#b8c2cf" stroke-width="1.4" stroke-dasharray="6,7" opacity="0.7"/>

            <!-- ring arcs -->
            <path v-for="arc in deptArcPaths" :key="'arc'+arc.id"
              :d="arc.d" fill="none" :stroke="'url(#'+arc.gradId+')'"
              stroke-width="9" stroke-linecap="round" opacity="0.9"/>

            <!-- tiles + connectors -->
            <g v-for="td in deptTileData" :key="'tile'+td.t.id">
              <line :x1="td.lx" :y1="td.ly" :x2="td.t.cx" :y2="td.t.cy"
                :stroke="td.t.color" stroke-width="2" opacity="0.45"/>
              <rect :x="td.t.tile.x" :y="td.t.tile.y" :width="td.t.tile.w" :height="td.h"
                rx="13" fill="#fff" :stroke="td.t.color" stroke-width="1.5"/>
              <template v-for="row in td.textRows" :key="'row'+row.bi">
                <text :x="row.checkX" :y="row.checkY" font-size="15" font-weight="800"
                  :fill="td.t.color" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">✓</text>
                <text v-for="ln in row.lines" :key="'ln'+ln.y" :x="ln.x" :y="ln.y"
                  font-size="15" fill="#334155"
                  font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">{{ ln.text }}</text>
              </template>
            </g>

            <!-- nodes -->
            <g v-for="td in deptTileData" :key="'node'+td.t.id">
              <circle :cx="td.t.cx" :cy="td.t.cy" r="56" fill="#fff" :stroke="td.t.color" stroke-width="4.5"/>
              <text :x="td.t.cx" :y="td.t.cy + 10" text-anchor="middle" font-size="30"
                font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">{{ td.t.icon }}</text>
              <rect :x="td.t.cx - td.pw / 2" :y="td.t.cy + 42" :width="td.pw" height="30" rx="15" :fill="td.t.color"/>
              <text :x="td.t.cx" :y="td.t.cy + 62" text-anchor="middle" font-size="15" font-weight="800" fill="#fff"
                font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">{{ td.t.label }}</text>
            </g>

            <!-- center CONSTRA mark -->
            <circle cx="680" cy="480" r="54" fill="#fff" stroke="#2563eb" stroke-width="3"/>
          </svg>
          <img src="@/assets/img/constra/32543234_transparentO-4.png" class="dept-logo-overlay" alt="CONSTRA" />
        </div>

        <!-- Mobile: stacked cards -->
        <div class="dept-cards">
          <div v-for="t in deptTeams" :key="t.id" class="dept-mobile-card" :style="{ borderTopColor: t.color }">
            <div class="dept-mobile-name" :style="{ color: t.color }">{{ t.label }}</div>
            <div class="dept-mobile-bullets">
              <div v-for="b in t.bullets" :key="b" class="dept-mobile-bullet">
                <span class="dept-mobile-check" :style="{ color: t.color }">✓</span>
                <span>{{ b }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 8 — AI FEATURES -->
    <section class="section bg-white">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">AI Features</div>
          <h2>Experience the Power of AI</h2>
          <p>Four intelligent modules that transform raw site captures into actionable intelligence</p>
          <div class="section-rule"></div>
        </div>
        <div class="ai-grid">
          <div v-for="f in aiFeatures" :key="f.title" class="ai-card" :style="{ borderTopColor: f.col }">
            <div class="ai-card-header">
              <div class="ai-icon" :style="{ background: f.bg }">{{ f.icon }}</div>
              <div>
                <h3 class="ai-title">{{ f.title }}</h3>
                <div class="ai-sub" :style="{ color: f.col }">{{ f.sub }}</div>
              </div>
            </div>
            <div v-if="f.flow" class="ai-flow">
              <template v-for="(step, si) in f.flow" :key="step">
                <span class="ai-flow-step" :style="{ background: f.bg, color: f.col }">{{ step }}</span>
                <span v-if="si < f.flow.length - 1" class="ai-flow-arr">→</span>
              </template>
            </div>
            <div v-if="f.tags" class="ai-tags">
              <span v-for="t in f.tags" :key="t" class="ai-tag">{{ t }}</span>
            </div>
            <div class="ai-points">
              <div v-for="pt in f.points" :key="pt" class="ai-point">
                <span class="ai-point-check" :style="{ color: f.col }">✓</span>
                <span>{{ pt }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 9 — CRM VIDEO TOURS -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">CRM</div>
          <h2>CRM Video Tours</h2>
          <p>Generate personalized video tours for customers — directly from virtual tour data</p>
          <div class="section-rule"></div>
        </div>
        <div class="crm-grid">
          <div v-for="step in crmSteps" :key="step.n" class="crm-card">
            <div class="crm-num">{{ step.n }}</div>
            <h4 class="crm-title">{{ step.title }}</h4>
            <p class="crm-desc">{{ step.desc }}</p>
          </div>
        </div>
        <p class="crm-note">Clients can exclude specific areas and re-sequence the tour. Video tours play sequentially or interactively.</p>
      </div>
    </section>

    <!-- 10 — INTEGRATIONS -->
    <section class="section bg-white">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Integrations</div>
          <h2>Data Flows Seamlessly Between Platforms</h2>
          <p>CONSTRA unites your tech stack — connecting design, scheduling, and customer-facing systems</p>
          <div class="section-rule"></div>
        </div>
        <div class="integ-svg-wrap">
        <svg class="integ-svg" viewBox="0 0 820 480" role="img"
          aria-label="CONSTRA as the central hub connecting Autodesk Construction Cloud, Microsoft Project, Consumer App and other systems">
          <defs>
            <marker id="integ-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0 0 L10 5 L0 10 z" fill="#94a3b8"/>
            </marker>
            <filter id="integ-card-sh" x="-20%" y="-20%" width="140%" height="160%">
              <feDropShadow dx="0" dy="3" stdDeviation="6" flood-color="#0f172a" flood-opacity="0.13"/>
            </filter>
          </defs>

          <!-- dashed connectors -->
          <line v-for="(l, i) in integLinks" :key="'il'+i"
            :x1="l[0]" :y1="l[1]" :x2="l[2]" :y2="l[3]"
            stroke="#94a3b8" stroke-width="1.6" stroke-dasharray="5,5"
            marker-start="url(#integ-arrow)" marker-end="url(#integ-arrow)"/>

          <!-- platform cards -->
          <g v-for="(c, i) in integCards" :key="'ic'+i">
            <rect :x="c.x" :y="c.y" width="240" height="148" rx="14" fill="#fff" filter="url(#integ-card-sh)"/>
            <rect :x="c.x + 98" :y="c.y + 16" width="44" height="44" rx="11" :fill="c.bg"/>
            <text :x="c.x + 120" :y="c.y + 46" text-anchor="middle" font-size="22"
              font-family="sans-serif">{{ c.icon }}</text>
            <text v-for="(t, k) in c.title" :key="'ct'+k"
              :x="c.x + 120" :y="c.y + 86 + k * 18"
              text-anchor="middle" font-size="14.5" font-weight="800" fill="#003366"
              font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">{{ t }}</text>
            <text :x="c.x + 120" :y="c.y + 86 + c.title.length * 18 + 4"
              text-anchor="middle" font-size="11.5" fill="#64748b"
              font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">{{ c.sub }}</text>
            <line :x1="c.x + 16" :y1="c.y + 145" :x2="c.x + 224" :y2="c.y + 145"
              :stroke="c.accent" stroke-width="4" stroke-linecap="round"/>
          </g>

          <!-- CONSTRA hub -->
          <rect x="355" y="185" width="110" height="110" rx="14" fill="#fff" filter="url(#integ-card-sh)"/>
        </svg>
        <img src="@/assets/img/constra/32543234_transparentO-4.png" class="integ-logo-overlay" alt="CONSTRA" />
        </div>

        <div class="integ-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="flex-shrink:0">
            <path d="M20 8a8 8 0 0 0-14-3M4 6v4h4M4 16a8 8 0 0 0 14 3M20 18v-4h-4"
              stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Data seamlessly flows between platforms — keeping your entire tech ecosystem in sync.
        </div>
      </div>
    </section>

    <!-- 11 — SURVEY -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Survey</div>
          <h2>Fully Digital Land Mapping</h2>
          <p>Drone-based land survey and terrain analytics — view your land in 2D, 3D, and everything in between</p>
          <div class="section-rule"></div>
        </div>
        <div class="survey-grid">
          <div class="survey-checklist">
            <h3 class="survey-checklist-title">Survey Outputs &amp; Tools</h3>
            <div class="survey-features">
              <div v-for="f in surveyFeatures" :key="f" class="survey-feature">
                <span class="survey-check">✓</span>
                <span>{{ f }}</span>
              </div>
            </div>
          </div>
          <div class="survey-visual">
            <svg viewBox="0 0 320 210" width="100%" style="max-width:300px">
              <line v-for="x in [40,80,120,160,200,240,280]" :key="'vg'+x" :x1="x" y1="10" :x2="x" y2="195" stroke="rgba(255,255,255,0.06)" stroke-width="1"/>
              <line v-for="y in [50,90,130,170]" :key="'hg'+y" x1="15" :y1="y" x2="305" :y2="y" stroke="rgba(255,255,255,0.06)" stroke-width="1"/>
              <ellipse v-for="(c, i) in [0,1,2,3,4]" :key="'c'+i" cx="160" cy="105" :rx="136-i*22" :ry="68-i*11" fill="none" :stroke="`rgba(158,234,34,${0.14+i*0.13})`" :stroke-width="0.8+i*0.35"/>
              <g transform="translate(146,60)">
                <rect x="0" y="8" width="26" height="9" rx="2" fill="rgba(255,255,255,0.88)"/>
                <rect x="-13" y="4" width="11" height="5" rx="2" fill="rgba(158,234,34,0.85)"/>
                <rect x="28" y="4" width="11" height="5" rx="2" fill="rgba(158,234,34,0.85)"/>
                <circle cx="-7" cy="4" r="3.5" fill="rgba(255,255,255,0.65)"/>
                <circle cx="34" cy="4" r="3.5" fill="rgba(255,255,255,0.65)"/>
                <line x1="13" y1="17" x2="13" y2="28" stroke="rgba(158,234,34,0.5)" stroke-width="1" stroke-dasharray="2,2"/>
              </g>
              <circle v-for="(pt, i) in surveyPoints" :key="'d'+i" :cx="pt[0]" :cy="pt[1]" r="2.5" :fill="`rgba(158,234,34,${0.35+i*0.055})`"/>
              <text x="160" y="198" text-anchor="middle" fill="rgba(255,255,255,0.4)" font-size="10" font-family="sans-serif" letter-spacing="0.5">CONSTRA SURVEY — 3D TERRAIN VIEW</text>
            </svg>
            <p class="survey-visual-caption">Superimpose CAD drawings on orthoimages. Get highly accurate topographical outputs for analysis.</p>
            <router-link to="/solutions/constra_survey" class="survey-link">Explore Full Survey →</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 12 — CASE STUDIES -->
    <section class="section bg-white">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Case Studies</div>
          <h2>Real-World Implementations</h2>
          <p>How leading developers are using CONSTRA to transform project operations</p>
          <div class="section-rule"></div>
        </div>
        <div class="cases-grid">
          <div v-for="c in cases" :key="c.client" class="case-card">
            <div class="case-header">
              <div class="case-badge" :style="{ background: c.bg, color: c.col }">{{ c.badge }}</div>
              <div class="case-client">{{ c.client }}</div>
            </div>
            <div class="case-body">
              <div class="case-row">
                <div class="case-row-label" style="color:#ef4444;">CHALLENGE</div>
                <p class="case-row-text">{{ c.problem }}</p>
              </div>
              <div class="case-row">
                <div class="case-row-label" style="color:#2563eb;">SOLUTION</div>
                <p class="case-row-text">{{ c.solution }}</p>
              </div>
              <div class="case-row">
                <div class="case-row-label" style="color:#16a34a;">RESULT</div>
                <p class="case-row-text">{{ c.result }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 13 — VIDEO TESTIMONIALS -->
    <section class="section bg-alt">
      <div class="section-container">
        <div class="section-header">
          <div class="section-pill">Testimonials</div>
          <h2>What Our Clients Say</h2>
          <p>Hear directly from the teams using CONSTRA on their projects</p>
          <div class="section-rule"></div>
        </div>
        <div class="testimonials-grid">
          <div v-for="t in testimonials" :key="t.embedId" class="testimonial-card">
            <div class="testimonial-video-wrap">
              <iframe
                :src="`https://www.youtube.com/embed/${t.embedId}`"
                :title="`${t.name} — CONSTRA Testimonial`"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <div class="testimonial-info">
              <div class="testimonial-name">{{ t.name }}</div>
              <div class="testimonial-role">{{ t.role }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER CTA -->
    <section class="footer-cta">
      <div class="hero-dot-grid"></div>
      <div class="section-container footer-cta-inner">
        <div class="footer-cta-eyebrow">GET STARTED</div>
        <h2 class="footer-cta-title">Ready to eliminate manual reporting?</h2>
        <p class="footer-cta-sub">See how CONSTRA transforms construction operations with AI — drones, 360° cameras, and zero manual entry.</p>
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSdQditzMzSBuq2J2urqc2OR28cjAFdTitdSHlOyXdHp6N2rfQ/viewform" target="_blank" class="cta-btn">Request Demo →</a>
        <div class="footer-cta-byline">by Bandhoo</div>
      </div>
    </section>

  </main>
</template>

<script>
import InfiniteScroll from "@/components/InfiniteScroll.vue"
import { GoogleAdsConversions } from '@/assets/db/config'
import { constraClients } from '@/assets/db/static-data'

export default {
  name: 'ConstraRedesign',
  components: { InfiniteScroll },
  data() {
    return {
      constraClients,
      heroStats: [
        { val: '25+', txt: 'Enterprise Clients' },
        { val: '12', txt: 'AI-Powered Modules' },
        { val: '360°', txt: 'Visual Coverage' },
        { val: 'Zero', txt: 'Manual Entry' },
      ],
      scaleStats: [
        { num: '~1 Million', label: 'Tasks per project lifetime', detail: '900,000 status updates needed over 30 months', color: '#ef4444' },
        { num: '~30,000', label: 'Space updates every month', detail: 'Every space across every tower needs tracking', color: '#f97316' },
        { num: '5+', label: 'Active tasks at any given time', detail: 'Only 5 spaces active — yet all 30k need logging', color: '#eab308' },
      ],
      opsSteps: [
        { n: 1, title: 'Autodesk BIM', sub: 'Document Management', type: 'third' },
        { n: 2, title: 'Microsoft Projects', sub: 'Plans & Schedule', type: 'third' },
        { n: 3, title: 'Progress & Housekeeping', sub: 'CONSTRA Module', type: 'constra' },
        { n: 4, title: 'Quality Management', sub: 'CONSTRA Module', type: 'constra' },
        { n: 5, title: 'Safety Monitoring', sub: 'CONSTRA Module', type: 'constra' },
        { n: 6, title: 'Consumer Updates', sub: 'CONSTRA Module', type: 'constra' },
      ],
      benefits: [
        { n: 1, title: 'CAD Overlay', desc: 'Accurate CAD overlay on every slab & verticalization check' },
        { n: 2, title: 'Excavation Volumes', desc: 'Accurate excavation volume calculations from drone data' },
        { n: 3, title: 'RA Bill Certification', desc: '100% visual evidence for monthly RA bill certification' },
        { n: 4, title: 'CRM Video Updates', desc: 'Unit-specific videos for customers via CRM' },
        { n: 5, title: 'AI Progress Reports', desc: 'AI-driven automated physical progress reports against WBS' },
        { n: 6, title: 'Finishing Defects AI', desc: 'AI-based finishing defects assistant' },
        { n: 7, title: 'Civil Defects AI', desc: 'AI-based civil defects detection and management' },
        { n: 8, title: 'Housekeeping AI', desc: 'AI-based housekeeping report & field issue mapping' },
        { n: 9, title: 'Safety Reporting', desc: 'AI-based comprehensive safety reporting per capture' },
        { n: 10, title: 'Digital Terrain Model', desc: 'Digital terrain & surface model with elevation profile' },
        { n: 11, title: 'Tool Integration', desc: 'Integration with Microsoft Projects and Autodesk ACC' },
        { n: 12, title: 'Handover Checklists', desc: 'Digital handover checklist for each space/unit' },
      ],
      stages: ['Design', 'Excavation', 'Structure', 'Build', 'MEP', 'Finishing / Handover'],
      stageStyles: [
        { bg: '#e0f2fe', col: '#0369a1' },
        { bg: '#dcfce7', col: '#166534' },
        { bg: '#fef9c3', col: '#854d0e' },
        { bg: '#fce7f3', col: '#9d174d' },
        { bg: '#ede9fe', col: '#5b21b6' },
        { bg: '#fff7ed', col: '#9a3412' },
      ],
      droneFreq: ['Once', 'Fortnightly', 'Monthly', 'Monthly', 'Monthly', 'Monthly'],
      camFreq: ['—', '—', 'Monthly', 'Monthly', 'Fortnightly / Monthly', 'Monthly'],
      tableFeatures: [
        ['Ortho images', 'DTM', 'DSM', 'Stream Flows'],
        ['Ortho images', 'Excavation Volume', '3D Point Cloud'],
        ['Ortho images', 'CAD Overlays per slab', '3D Point Cloud'],
        ['Field Issues', 'Punch List', 'Progress Tracker', '3D Point Cloud'],
        ['3D Point Cloud', 'Checklists', 'Punch / Snag List', 'Progress Tracker', 'CRM Updates'],
        ['Checklists', 'Snag List', 'Progress Tracker', 'CRM Updates', 'Handover Videos'],
      ],
      deptTeams: [
        { id: 'site', cx: 680, cy: 248, color: '#2563eb', label: 'Site Team', icon: '🏗️', tile: { x: 516, y: 34, w: 328 }, side: 'top', bullets: ['No manual data entry', 'HQ has live access through visuals', 'RA Bill certification from accurate visuals'] },
        { id: 'crm', cx: 968, cy: 372, color: '#7c3aed', label: 'CRM Team', icon: '🤝', tile: { x: 1062, y: 300, w: 286 }, side: 'right', bullets: ['Easy access to videos & images', 'Share with customers without chasing the site team', 'Unit-specific video tours on demand'] },
        { id: 'hq', cx: 968, cy: 600, color: '#dc2626', label: 'HQ Team', icon: '🏢', tile: { x: 1062, y: 528, w: 286 }, side: 'right', bullets: ['Monthly work validation', 'Fewer required site visits', 'Real-time project visibility from HQ'] },
        { id: 'design', cx: 680, cy: 724, color: '#0d9488', label: 'Design Team', icon: '📐', tile: { x: 516, y: 806, w: 328 }, side: 'bottom', bullets: ['CAD overlays after each slab completion', 'BIM vs actual works comparison', 'Design-to-execution consistency checks'] },
        { id: 'proc', cx: 392, cy: 600, color: '#ea580c', label: 'Procurement Team', icon: '📦', tile: { x: 12, y: 528, w: 286 }, side: 'left', bullets: ['Work front availability assessment', 'Visual validation of material usage', 'Progress-linked procurement planning'] },
        { id: 'qual', cx: 392, cy: 372, color: '#16a34a', label: 'Quality Team', icon: '✅', tile: { x: 12, y: 300, w: 286 }, side: 'left', bullets: ['AI-based snag detection', 'Remote supervision of work quality', 'Monitor site conditions from anywhere'] },
      ],
      deptRingPairs: [['site', 'crm'], ['crm', 'hq'], ['hq', 'design'], ['design', 'proc'], ['proc', 'qual'], ['qual', 'site']],
      deptInnerPairs: [['site', 'design'], ['qual', 'hq'], ['crm', 'proc'], ['qual', 'crm'], ['proc', 'hq']],
      integCards: [
        { x: 0, y: 0, title: ['Autodesk', 'Construction Cloud'], sub: 'Issues & Documents', accent: '#2563eb', bg: '#dbeafe', icon: '🔷' },
        { x: 580, y: 0, title: ['Consumer App'], sub: 'CRM Updates', accent: '#16a34a', bg: '#dcfce7', icon: '📱' },
        { x: 0, y: 332, title: ['Microsoft Project'], sub: 'Schedule & WBS', accent: '#16a34a', bg: '#dcfce7', icon: '📋' },
        { x: 580, y: 332, title: ['Other Systems'], sub: 'ERP / Finance / Custom', accent: '#7c3aed', bg: '#ede9fe', icon: '⚙️' },
      ],
      integLinks: [
        [254, 152, 351, 184], [566, 152, 469, 184], [254, 328, 351, 296], [566, 328, 469, 296],
      ],
      aiFeatures: [
        {
          title: 'Progress Monitoring', icon: '🏗️', col: '#2563eb', bg: '#eff6ff',
          sub: 'Progress tracking & documentation', flow: null, tags: null,
          points: ['3D point cloud of the building', 'Side-by-side progress comparison over time', 'Field Issues / Punch List / Snags', 'Virtual tour of each floor & building'],
        },
        {
          title: 'AI Defect Manager', icon: '🔍', col: '#dc2626', bg: '#fff1f1',
          sub: 'Civil and finishing defects workflow',
          flow: ['Manual Input', 'AI Detection', 'Defect Mgmt', 'Assignment', 'Resolution'],
          tags: null,
          points: ['Manual + AI-detected defects unified', 'AI agent co-built with client checklists', 'HQ team accepts/rejects remotely', '5–8 min per flat for final inspection only'],
        },
        {
          title: 'Safety Assistant', icon: '🦺', col: '#f97316', bg: '#fff7ed',
          sub: 'AI-based safety monitoring',
          flow: ['Virtual Tour', 'AI Analysis', 'Safety Dashboards', 'Compliance Report'],
          tags: ['PPE', 'Railings', 'Scaffolding', 'Fall Protection'],
          points: ['Analyzes every virtual tour image automatically', 'Auto-generates dashboards and violation reports', 'Violation categories tracked per capture', 'Compliance trends over time'],
        },
        {
          title: 'Housekeeping Assistant', icon: '🧹', col: '#16a34a', bg: '#f0fdf4',
          sub: 'Site cleanliness & organization',
          flow: ['Virtual Tour', 'AI Analysis', 'HK Dashboards', 'HK Report'],
          tags: ['Debris', 'Materials', 'Tools', 'Storage'],
          points: ['Tracks debris, materials, and tools', 'Auto-reports from every virtual tour', 'Violation breakdown by category', 'Trend analysis for ongoing improvement'],
        },
      ],
      crmSteps: [
        { n: 1, title: 'Virtual Tour', desc: 'Start with a complete site virtual tour' },
        { n: 2, title: 'Select Flat', desc: 'Choose the specific flat or unit' },
        { n: 3, title: 'Select Sections', desc: 'Pick areas: Living Room, Kitchen, Bedroom…' },
        { n: 4, title: 'Choose Sequence', desc: "Arrange in the customer's preferred order" },
        { n: 5, title: 'Generate URL', desc: 'Auto-generate a smooth video tour link' },
        { n: 6, title: 'Customer Plays', desc: 'View sequentially or interact like Constra' },
      ],
      surveyFeatures: [
        'Orthomosaic Images', 'Contours', 'Grid Levels',
        'Features Digitization', '3D Point Cloud', 'Stream-flow Simulation',
        'Digital Surface Model (DSM)', 'Digital Terrain Model (DTM)',
        'Distance Measurement Tool', 'Area Measurement Tool',
        'Volumetric Analysis Tool', 'Elevation Profile Tool',
      ],
      surveyPoints: [
        [55, 155], [75, 140], [100, 158], [125, 145], [155, 152],
        [178, 140], [205, 148], [230, 138], [258, 142], [278, 133],
      ],
      cases: [
        {
          client: 'My Home Constructions', badge: 'MPP Integration · S-Curve', col: '#1d4ed8', bg: '#dbeafe',
          problem: 'MPP used for scheduling with no visual progress tracking. Teams relied on separate tools and manual reports to correlate data.',
          solution: 'Built a module consuming MPP files directly into Constra. Drill-down into any schedule hierarchy level alongside visual site data.',
          result: 'Unified dashboard with S-Curve reports + MPP progress. No more switching between tools or generating separate presentations.',
        },
        {
          client: 'Oberoi Realty', badge: 'ACC Issues Integration', col: '#b45309', bg: '#fef3c7',
          problem: 'All contractors on Autodesk Construction Cloud (ACC) for issues. Constra had a similar module — causing duplicate entry across platforms.',
          solution: 'Integrated ACC issues module with Constra. Issues sync in real time; status, comments, and resolutions flow back from ACC.',
          result: 'AI-detected defects auto-create as ACC issues upon approval. Seamless end-to-end quality pipeline across both platforms.',
        },
        {
          client: 'Marathon Realty', badge: 'CRM Video Tours', col: '#166534', bg: '#dcfce7',
          problem: 'Wanted customized video tours from virtual tours for their customer app. No existing integration for property-specific visual content.',
          solution: 'API-based integration for property-specific details per flat. Customized video tours from Constra play directly on the consumer app.',
          result: 'Immersive property tours for buyers. Transparency with home buyers. CRM synced with CLP construction-linked payment milestones.',
        },
      ],
      testimonials: [
        { name: 'Pankaj Pandit', role: 'Client Testimonial', embedId: '2zj1vpBADlk' },
        { name: 'Saurabh Jolly', role: 'Client Testimonial', embedId: '97RmHox8JyA' },
      ],
    }
  },
  computed: {
    deptTeamMap() {
      return Object.fromEntries(this.deptTeams.map(t => [t.id, t]));
    },
    deptArcPaths() {
      return this.deptRingPairs.map(([aId, bId]) => {
        const a = this.deptTeamMap[aId], b = this.deptTeamMap[bId];
        return {
          id: aId + bId,
          gradId: `dept-arc-${aId}-${bId}`,
          d: this.arcPath(a, b),
          x1: a.cx, y1: a.cy, x2: b.cx, y2: b.cy,
          startColor: a.color, endColor: b.color,
        };
      });
    },
    deptTileData() {
      return this.deptTeams.map(t => {
        const wrapped = t.bullets.map(b => this.wrapText(b, 26));
        let h = 22;
        wrapped.forEach(ls => { h += ls.length * 20 + 12; });
        h += 6;
        const tx = t.tile.x, ty = t.tile.y, tw = t.tile.w;
        let lx = 0, ly = 0;
        if (t.side === 'top')    { lx = tx + tw / 2; ly = ty + h; }
        if (t.side === 'bottom') { lx = tx + tw / 2; ly = ty; }
        if (t.side === 'left')   { lx = tx + tw;     ly = ty + h / 2; }
        if (t.side === 'right')  { lx = tx;          ly = ty + h / 2; }
        const textRows = [];
        let cursorY = ty + 26;
        wrapped.forEach((ls, bi) => {
          textRows.push({
            bi,
            checkX: tx + 18, checkY: cursorY, color: t.color,
            lines: ls.map((ln, li) => ({ text: ln, x: tx + 38, y: cursorY + li * 20 })),
          });
          cursorY += ls.length * 20 + 12;
        });
        const pw = t.label.length * 9.2 + 30;
        return { t, h, lx, ly, textRows, pw };
      });
    },
  },
  methods: {
    arcPath(a, b) {
      const CXC = 680, CYC = 480;
      const mx = (a.cx + b.cx) / 2, my = (a.cy + b.cy) / 2;
      let dx = mx - CXC, dy = my - CYC;
      const d = Math.hypot(dx, dy) || 1;
      dx /= d; dy /= d;
      const cx = mx + dx * 64, cy = my + dy * 64;
      return `M${a.cx} ${a.cy} Q${cx} ${cy} ${b.cx} ${b.cy}`;
    },
    wrapText(str, max) {
      const words = str.split(' ');
      const lines = [];
      let cur = '';
      words.forEach(w => {
        if ((cur + ' ' + w).trim().length > max) { if (cur) lines.push(cur); cur = w; }
        else cur = (cur + ' ' + w).trim();
      });
      if (cur) lines.push(cur);
      return lines;
    },
  },
  mounted() {
    this.$gtag.event("constra_homepage");
    this.$gtag.event('conversion', GoogleAdsConversions.CONSTRA_PAGE_VISIT);
  },
}
</script>

<style scoped>
/* ── BASE — mobile first ──────────────────────────────────────────────────── */
.constra-page {
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  color: var(--cc-body);
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.section { padding: 48px 0; }
.bg-white { background: var(--cc-bg-white); }
.bg-alt   { background: var(--cc-bg-alt); }

@media (min-width: 768px) {
  .section-container { padding: 0 24px; }
  .section { padding: 72px 0; }
}

/* ── SECTION HEADER ───────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-pill {
  display: inline-block;
  background: var(--cc-lime-bg);
  color: var(--cc-lime-text);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 4px 13px;
  border-radius: 20px;
  margin-bottom: 14px;
}

.section-header h2 {
  font-size: 22px;
  font-weight: 900;
  color: var(--cc-navy);
  margin: 0 0 12px;
  line-height: 1.2;
  letter-spacing: -0.4px;
}

.section-header p {
  color: var(--cc-body);
  font-size: 15px;
  max-width: 580px;
  margin: 0 auto;
  line-height: 1.65;
}

.section-rule {
  width: 36px;
  height: 3px;
  background: var(--cc-lime);
  border-radius: 2px;
  margin: 16px auto 0;
}

@media (min-width: 768px) {
  .section-header { margin-bottom: 52px; }
  .section-header h2 { font-size: clamp(22px, 2.8vw, 32px); }
  .section-header p  { font-size: 16px; }
}

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.hero {
  background: linear-gradient(155deg, var(--cc-navy-light) 0%, var(--cc-navy) 50%, var(--cc-navy-deep) 100%);
  padding: 36px 16px 48px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-dot-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px);
  background-size: 26px 26px;
}

.hero-glow {
  position: absolute;
  bottom: -80px;
  left: 50%;
  transform: translateX(-50%);
  width: 700px;
  height: 240px;
  background: radial-gradient(ellipse, rgba(158,234,34,0.095) 0%, transparent 70%);
  pointer-events: none;
}

.hero-inner { position: relative; }

.hero-wordmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}

.hero-logo-img {
  max-width: 160px;
  height: auto;
}

.hero-eyebrow {
  color: rgba(255,255,255,0.4);
  font-size: 10px;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.hero-headline {
  color: #fff;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.15;
  margin: 0 auto 16px;
  max-width: 880px;
  letter-spacing: -0.5px;
}

.hero-sub {
  color: rgba(255,255,255,0.6);
  font-size: 15px;
  margin-bottom: 36px;
  font-weight: 400;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--cc-lime);
  color: var(--cc-navy);
  border: none;
  border-radius: 12px;
  padding: 14px 36px;
  font-weight: 900;
  font-size: 15px;
  cursor: pointer;
  letter-spacing: 0.3px;
  text-decoration: none;
  box-shadow: 0 6px 22px rgba(158,234,34,0.38);
  transition: background 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
  font-family: inherit;
}

.cta-btn:hover {
  background: var(--cc-lime-hover);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 16px 40px rgba(158,234,34,0.55);
}

.hero-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 28px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.hero-stat-val {
  color: var(--cc-lime);
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
}

.hero-stat-txt {
  color: rgba(255,255,255,0.45);
  font-size: 11px;
  letter-spacing: 1.4px;
  margin-top: 6px;
  text-transform: uppercase;
  text-align: center;
}

@media (min-width: 480px) {
  .hero-wordmark { flex-direction: row; gap: 14px; }
}

@media (min-width: 768px) {
  .hero { padding: clamp(40px, 5vw, 64px) 24px clamp(56px, 7vw, 88px); }
  .hero-headline { font-size: clamp(34px, 5.2vw, 60px); letter-spacing: -1.2px; }
  .hero-sub      { font-size: clamp(16px, 2vw, 22px); margin-bottom: 48px; }
  .cta-btn       { padding: 18px 52px; font-size: 17px; }
  .hero-stats    { gap: clamp(24px, 5vw, 84px); margin-top: 72px; padding-top: 40px; }
  .hero-stat-val { font-size: 36px; }
}

/* ── CLIENT LOGOS ─────────────────────────────────────────────────────────── */
.client-logos-section {
  background: var(--cc-bg-white);
  padding: 8px 0;
  border-top: 1px solid var(--cc-border);
  border-bottom: 1px solid var(--cc-border);
  overflow: hidden;
}

/* ── PROBLEM STATEMENT ────────────────────────────────────────────────────── */
.scale-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.scale-card {
  background: var(--cc-bg-white);
  border-radius: 16px;
  padding: 24px 20px;
  border-left: 5px solid;
  box-shadow: var(--cc-shadow);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.scale-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--cc-shadow-hover);
}

.scale-num   { font-size: 36px; font-weight: 900; line-height: 1; margin-bottom: 10px; }
.scale-label { font-size: 16px; font-weight: 700; color: var(--cc-heading); margin-bottom: 6px; }
.scale-detail { font-size: 13px; color: var(--cc-body); line-height: 1.55; }

.problem-callout {
  background: var(--cc-navy);
  border-radius: 16px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
}

.callout-text { flex: 1; }

.callout-eyebrow {
  color: var(--cc-lime);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.callout-text p {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
}

.callout-zero {
  background: var(--cc-lime);
  border-radius: 14px;
  padding: 18px 32px;
  text-align: center;
  flex-shrink: 0;
}

.zero-num   { color: var(--cc-navy); font-size: 40px; font-weight: 900; line-height: 1; }
.zero-label { color: var(--cc-navy); font-size: 11px; font-weight: 800; margin-top: 5px; letter-spacing: 0.5px; }

@media (min-width: 480px) {
  .scale-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
  .scale-num  { font-size: 48px; }
}

@media (min-width: 768px) {
  .scale-grid      { gap: 20px; margin-bottom: 32px; }
  .scale-card      { padding: 36px 30px; }
  .scale-label     { font-size: 17px; }
  .problem-callout { flex-direction: row; align-items: center; text-align: left; padding: 28px 36px; justify-content: space-between; }
  .callout-text p  { font-size: 19px; max-width: 520px; }
  .zero-num        { font-size: 48px; }
  .callout-zero    { padding: 22px 36px; }
}

/* ── OPS ARCHITECTURE ─────────────────────────────────────────────────────── */
.ops-labels { display: none; }

.ops-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.ops-step {
  border-radius: 14px;
  padding: 18px 10px;
  text-align: center;
  transition: transform 0.25s;
}

.ops-step:hover { transform: translateY(-4px); }
.ops-step--third   { background: var(--cc-third-bg); border: 2px solid var(--cc-border-light); }
.ops-step--constra { background: var(--cc-navy);     border: 2px solid var(--cc-navy-border); }

.ops-num {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
  margin: 0 auto 10px;
}

.ops-num--third   { background: var(--cc-third-num); color: var(--cc-third-text); }
.ops-num--constra { background: var(--cc-lime);       color: var(--cc-navy); }

.ops-title { font-size: 11px; font-weight: 700; color: var(--cc-heading); line-height: 1.35; margin-bottom: 4px; }
.ops-title--constra { color: #ffffff; }

.ops-sub { font-size: 10px; font-weight: 500; color: var(--cc-muted); letter-spacing: 0.2px; }
.ops-sub--constra { color: rgba(255,255,255,0.5); }

@media (min-width: 480px) {
  .ops-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 768px) {
  .ops-labels { display: grid; grid-template-columns: 2fr 4fr; gap: 8px; margin-bottom: 8px; }
  .ops-grid   { grid-template-columns: repeat(6, 1fr); }
  .ops-step   { padding: 22px 12px; }
  .ops-title  { font-size: 12px; }
  .ops-label-third,
  .ops-label-constra {
    text-align: center;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 7px 0;
    border-radius: 8px;
  }
  .ops-label-third   { color: var(--cc-third-text); border: 1px dashed var(--cc-border-dashed); }
  .ops-label-constra { color: var(--cc-lime); background: var(--cc-navy); }
}

/* ── 12 BENEFITS ──────────────────────────────────────────────────────────── */
.benefits-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.benefit-card {
  background: var(--cc-bg-white);
  border-radius: 14px;
  padding: 22px 20px;
  box-shadow: var(--cc-shadow);
  cursor: default;
  position: relative;
  overflow: hidden;
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--cc-shadow-hover);
}

.benefit-watermark {
  position: absolute;
  right: -4px;
  top: -6px;
  font-size: 88px;
  font-weight: 900;
  line-height: 1;
  color: rgba(0,51,102,0.04);
  user-select: none;
  pointer-events: none;
  transition: color 0.28s;
}

.benefit-card:hover .benefit-watermark { color: rgba(0,51,102,0.07); }

.benefit-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--cc-lime-bg);
  color: var(--cc-navy);
  font-weight: 900;
  font-size: 14px;
  margin-bottom: 12px;
  position: relative;
  transition: background 0.28s;
}

.benefit-card:hover .benefit-badge { background: var(--cc-lime); }

.benefit-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--cc-heading);
  margin: 0 0 7px;
  line-height: 1.3;
  position: relative;
}

.benefit-desc {
  font-size: 13px;
  color: var(--cc-body);
  margin: 0;
  line-height: 1.6;
  position: relative;
}

@media (min-width: 480px) {
  .benefits-grid { grid-template-columns: repeat(2, 1fr); }
  .benefit-card  { padding: 26px 24px; }
}

@media (min-width: 1024px) {
  .benefits-grid { grid-template-columns: repeat(3, 1fr); gap: 20px; }
}

/* ── CAPTURE FREQUENCY TABLE ──────────────────────────────────────────────── */
.table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 14px;
}

.capture-table {
  width: 100%;
  min-width: 680px;
  border-collapse: separate;
  border-spacing: 4px;
  font-size: 13px;
}

.capture-table th { font-weight: 700; padding: 12px 10px; border-radius: 8px; }

.table-row-label-th { background: transparent; width: 140px; }

.table-row-label {
  background: var(--cc-bg-alt);
  font-weight: 700;
  min-width: 130px;
  white-space: nowrap;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
}

.table-row-sub { font-size: 10px; color: var(--cc-muted); font-weight: 400; margin-top: 2px; }

.table-freq-cell { padding: 12px 11px; border-radius: 8px; text-align: center; font-weight: 700; }

.freq-empty { background: #fafafa; color: #cbd5e1; font-weight: 400; }
.freq-drone { background: #f0fdf4; color: #166534; }
.freq-cam   { background: #f5f3ff; color: #5b21b6; }

.table-feat-cell { padding: 12px 11px; border-radius: 8px; background: #fafafa; vertical-align: top; }

.feat-tag {
  display: inline-block;
  margin: 2px;
  background: var(--cc-bg-input);
  border: 1px solid var(--cc-border-light);
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  color: var(--cc-body);
  font-weight: 500;
}

/* ── DEPARTMENT BENEFITS ──────────────────────────────────────────────────── */
.dept-figure { width: 100%; overflow-x: auto; display: none; position: relative; }
.dept-svg    { width: 100%; height: auto; display: block; }

.dept-logo-overlay {
  position: absolute;
  /* circle center: (680/1360, 480/1012) = (50%, 47.4%) of viewBox */
  left: 50%;
  top: 47.4%;
  transform: translate(-50%, -50%);
  /* ~88px wide in 1360px viewBox */
  width: 6.5%;
  pointer-events: none;
  filter: brightness(0);
}

.dept-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.dept-mobile-card {
  background: var(--cc-bg-white);
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: var(--cc-shadow);
  border-top: 4px solid;
}

.dept-mobile-name {
  font-size: 15px;
  font-weight: 800;
  margin-bottom: 12px;
}

.dept-mobile-bullets { display: flex; flex-direction: column; gap: 7px; }

.dept-mobile-bullet {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: var(--cc-body);
  line-height: 1.45;
}

.dept-mobile-check { font-weight: 800; font-size: 13px; flex-shrink: 0; line-height: 1.4; }

@media (min-width: 480px) {
  .dept-cards { grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 960px) {
  .dept-figure { display: block; }
  .dept-cards  { display: none; }
}

/* ── AI FEATURES ──────────────────────────────────────────────────────────── */
.ai-grid { display: grid; grid-template-columns: 1fr; gap: 20px; }

.ai-card {
  background: var(--cc-bg-white);
  border-radius: 16px;
  padding: 22px;
  box-shadow: var(--cc-shadow);
  border-top: 4px solid;
  cursor: default;
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.ai-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--cc-shadow-hover);
}

.ai-card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }

.ai-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ai-title { font-size: 16px; font-weight: 800; color: var(--cc-heading); margin: 0; }
.ai-sub   { font-size: 12px; font-weight: 600; margin-top: 2px; }

.ai-flow { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; margin: 12px 0; }

.ai-flow-step { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 5px; white-space: nowrap; }
.ai-flow-arr  { font-size: 11px; color: var(--cc-muted); flex-shrink: 0; }

.ai-tags { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 12px; }

.ai-tag {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 4px; border: 1px solid var(--cc-border-light);
  background: var(--cc-bg-alt); color: var(--cc-body);
}

.ai-points { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }

.ai-point { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--cc-body); line-height: 1.45; }
.ai-point-check { font-weight: 800; line-height: 1.4; flex-shrink: 0; }

@media (min-width: 768px) {
  .ai-grid { grid-template-columns: repeat(2, 1fr); gap: 24px; }
  .ai-card { padding: 28px; }
}

/* ── CRM VIDEO TOURS ──────────────────────────────────────────────────────── */
.crm-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.crm-card {
  border-radius: 14px;
  padding: 20px 12px;
  text-align: center;
  cursor: default;
  background: var(--cc-bg-white);
  box-shadow: var(--cc-shadow);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.crm-card:hover {
  background: var(--cc-navy);
  transform: translateY(-5px);
  box-shadow: var(--cc-shadow-hover);
}

.crm-num {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--cc-lime-bg);
  color: var(--cc-navy);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 18px;
  margin: 0 auto 12px;
  transition: background 0.25s ease;
}

.crm-card:hover .crm-num   { background: var(--cc-lime); }

.crm-title {
  font-size: 13px; font-weight: 800; color: var(--cc-heading);
  margin: 0 0 6px; line-height: 1.3; transition: color 0.25s ease;
}

.crm-card:hover .crm-title { color: #ffffff; }

.crm-desc {
  font-size: 11px; margin: 0; line-height: 1.55;
  color: var(--cc-body); transition: color 0.25s ease;
}

.crm-card:hover .crm-desc  { color: rgba(255,255,255,0.62); }

.crm-note { text-align: center; margin-top: 24px; font-size: 13px; color: var(--cc-muted); font-style: italic; }

@media (min-width: 768px) {
  .crm-grid  { grid-template-columns: repeat(3, 1fr); }
  .crm-title { font-size: 14px; }
  .crm-desc  { font-size: 12px; }
  .crm-card  { padding: 24px 14px; }
}

@media (min-width: 1200px) {
  .crm-grid { grid-template-columns: repeat(6, 1fr); }
}

/* ── INTEGRATIONS ─────────────────────────────────────────────────────────── */
.integ-svg-wrap {
  position: relative;
  max-width: 820px;
  margin: 0 auto;
}

.integ-logo-overlay {
  position: absolute;
  /* hub center: (410/820, 240/480) = (50%, 50%) of viewBox */
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  /* ~100px wide in 820px viewBox */
  width: 11%;
  pointer-events: none;
  filter: brightness(0);
}

.integ-svg {
  width: 100%;
  max-width: 820px;
  height: auto;
  display: block;
  margin: 0 auto;
  overflow: visible;
}

.integ-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
  font-size: 13px;
  color: var(--cc-body);
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 20px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

@media (min-width: 768px) {
  .integ-banner { font-size: 14px; }
}

/* ── SURVEY ───────────────────────────────────────────────────────────────── */
.survey-grid { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }

.survey-checklist {
  background: var(--cc-bg-white);
  border-radius: 16px;
  padding: 24px 20px;
  box-shadow: var(--cc-shadow);
}

.survey-checklist-title { font-size: 16px; font-weight: 800; color: var(--cc-navy); margin: 0 0 20px; }

.survey-features { display: grid; grid-template-columns: 1fr; gap: 10px; }

.survey-feature { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--cc-body); line-height: 1.45; }

.survey-check { color: var(--cc-lime); font-weight: 900; font-size: 15px; line-height: 1.3; flex-shrink: 0; }

.survey-visual {
  background: linear-gradient(140deg, var(--cc-navy-mid) 0%, var(--cc-navy) 60%, var(--cc-navy-deep) 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--cc-shadow);
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
}

.survey-visual-caption {
  color: rgba(255,255,255,0.55); font-size: 13px; text-align: center;
  margin: 18px 0 0; max-width: 260px; line-height: 1.55;
}

.survey-link {
  display: inline-block; margin-top: 18px; color: var(--cc-lime);
  font-size: 13px; font-weight: 700; text-decoration: none; letter-spacing: 0.3px; transition: opacity 0.2s;
}

.survey-link:hover { opacity: 0.8; }

@media (min-width: 480px) {
  .survey-features { grid-template-columns: 1fr 1fr; gap: 10px 16px; }
}

@media (min-width: 768px) {
  .survey-grid      { grid-template-columns: 1fr 1fr; gap: 36px; }
  .survey-checklist { padding: 32px; }
  .survey-visual    { min-height: 320px; padding: 36px 32px; }
}

/* ── CASE STUDIES ─────────────────────────────────────────────────────────── */
.cases-grid { display: grid; grid-template-columns: 1fr; gap: 20px; }

.case-card {
  background: var(--cc-bg-white);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--cc-shadow);
  display: flex;
  flex-direction: column;
  cursor: default;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.case-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--cc-shadow-hover);
}

.case-header { background: var(--cc-navy); padding: 20px 24px; }

.case-badge {
  display: inline-block; font-size: 10px; font-weight: 800;
  letter-spacing: 1.5px; text-transform: uppercase; padding: 3px 10px; border-radius: 20px; margin-bottom: 10px;
}

.case-client { color: #fff; font-size: 16px; font-weight: 900; line-height: 1.3; }

.case-body { padding: 20px 24px; flex: 1; }

.case-row { margin-bottom: 14px; }

.case-row-label { font-size: 9px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 5px; }
.case-row-text  { font-size: 13px; color: var(--cc-body); margin: 0; line-height: 1.58; }

@media (min-width: 768px) {
  .cases-grid { grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .case-header { padding: 24px 28px; }
  .case-client { font-size: 17px; }
  .case-body   { padding: 24px 28px; }
}

/* ── VIDEO TESTIMONIALS ───────────────────────────────────────────────────── */
.testimonials-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.testimonial-card {
  background: var(--cc-bg-white);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--cc-shadow);
}

.testimonial-video-wrap {
  position: relative;
  padding-top: 56.25%;
}

.testimonial-video-wrap iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.testimonial-info {
  padding: 16px 20px;
  border-top: 3px solid var(--cc-lime);
}

.testimonial-name { font-weight: 800; font-size: 15px; color: var(--cc-heading); }
.testimonial-role { font-size: 12px; color: var(--cc-muted); margin-top: 3px; }

@media (min-width: 768px) {
  .testimonials-grid { grid-template-columns: repeat(2, 1fr); gap: 28px; }
}

/* ── FOOTER CTA ───────────────────────────────────────────────────────────── */
.footer-cta {
  background: linear-gradient(155deg, var(--cc-navy-mid) 0%, var(--cc-navy) 50%, var(--cc-navy-deep) 100%);
  padding: 64px 16px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.footer-cta-inner { position: relative; max-width: 700px; margin: 0 auto; }

.footer-cta-eyebrow { color: var(--cc-lime); font-size: 10px; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 16px; }
.footer-cta-title   { color: #fff; font-size: 26px; font-weight: 900; margin: 0 0 14px; line-height: 1.2; letter-spacing: -0.5px; }
.footer-cta-sub     { color: rgba(255,255,255,0.58); font-size: 15px; margin: 0 0 32px; line-height: 1.6; }
.footer-cta-byline  { margin-top: 24px; font-size: 12px; color: rgba(255,255,255,0.32); }

@media (min-width: 768px) {
  .footer-cta       { padding: clamp(60px, 8vw, 88px) 24px; }
  .footer-cta-title { font-size: clamp(26px, 4vw, 44px); }
  .footer-cta-sub   { font-size: 17px; margin: 0 0 40px; }
}
</style>
